import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  Activity,
  currentActivitySettingProvider,
  ICurrentActivitySetting,
} from '../../../data/providers/settings/currentActivitySettingsProvider';
import { EntityType, IModelEntity } from '../../../data/types';
import { saveCurrentActivity } from '../../../data/writers/settings/currentActivitySetting';
import { KEYS } from '../../../keys';
import { Column, FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { AddAthletesPopup } from '../../pages/dashboard/athletes/AddAthletesPopup';
import { ManageGroupPopup } from '../../pages/dashboard/groups/ManageGroupPopup';
import { Avatar } from '../../shared/Avatar';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { Icon } from '../../shared/Icon';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { TabLabel, TabLabels } from '../../shared/tabs/Tabs';
import { SessionsCard } from '../sessions/SessionsCard';
import { Tests } from '../tests/Tests';
import { Analyze } from './analyze/Analyze';

interface IMatchParams {
  entityId: string;
  entityType: EntityType;
}
interface IProps extends RouteComponentProps<IMatchParams> {
  entity: IModelEntity;
  loading: boolean;
}

export const EntityView = withRouter((props: IProps): React.ReactElement => {
  const [activeTab, setActiveTab] = React.useState<Activity>(null);
  const [settingLoading, setSettingLoading] = React.useState<boolean>(true);

  /**
   * @description subscribe to the current activity setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = currentActivitySettingProvider.subscribe(
      (setting: ICurrentActivitySetting): void => {
        setSettingLoading(false);
        setActiveTab(setting.activityId);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description optimistically changes the users current activity setting
   * @param activityId
   */
  const handleActivityChange = (activityId: Activity): void => {
    setActiveTab(activityId);
    saveCurrentActivity({ activityId });
  };

  return (
    <Card elevation={0} tag='main' id='main'>
      <Column>
        {!isEmpty(props.entity) && !settingLoading ? (
          <React.Fragment>
            <CardHeader
              noPadding
              borderless
              style={{ background: 'var(--color-surface-0)' }}
            >
              <CardHeaderLeft style={{ padding: '10px' }}>
                <Avatar
                  size='list-avatar'
                  src={props.entity?.image}
                  imageToken={props.entity?.imageToken}
                />
                <Title>{props.entity?.name}</Title>
              </CardHeaderLeft>
              <CardHeaderRight
                style={{
                  flex: 2,
                }}
              >
                <TabLabels>
                  <TabLabel
                    activeElevation={6}
                    onClick={(): void => handleActivityChange('analyze')}
                    selected={'analyze' === activeTab}
                    data-testid={KEYS.ENTITY_VIEW_ANALYZE}
                  >
                    <Icon
                      name='assignment'
                      style={{ marginRight: '5px' }}
                      color={
                        activeTab === 'analyze'
                          ? 'var(--color-warning-accent)'
                          : 'var(--color-text)'
                      }
                    />
                    Reports
                  </TabLabel>
                  <TabLabel
                    activeElevation={6}
                    onClick={(): void => handleActivityChange('export')}
                    selected={'export' === activeTab}
                    data-testid={KEYS.ENTITY_VIEW_EXPORT}
                  >
                    <Icon
                      name='cloud_download'
                      style={{ marginRight: '5px' }}
                      color={
                        activeTab === 'export'
                          ? 'var(--color-warning-accent)'
                          : 'var(--color-text)'
                      }
                    />
                    Tests
                  </TabLabel>
                </TabLabels>
              </CardHeaderRight>
            </CardHeader>
            <CardBody
              id='Detail View'
              style={{ backgroundColor: 'var(--color-surface-6)' }}
            >
              {activeTab === 'export' ? (
                <Row shouldCollapse data-name='true'>
                  <FullHeightColumn style={{ flex: 70 }}>
                    <Tests
                      entity={props.entity}
                      entityType={props.match.params.entityType as EntityType}
                    />
                  </FullHeightColumn>
                  <FullHeightColumn style={{ flex: 30 }}>
                    <SessionsCard
                      entity={props.entity}
                      entityType={props.match.params.entityType as EntityType}
                    />
                  </FullHeightColumn>
                </Row>
              ) : (
                <Analyze
                  entity={props.entity}
                  entityType={props.match.params.entityType as EntityType}
                />
              )}
            </CardBody>
          </React.Fragment>
        ) : props.loading || settingLoading ? (
          <LinearLoading />
        ) : (
          <NoEntities entityType={props.match.params.entityType} />
        )}
      </Column>
    </Card>
  );
});

const Title = styled.h6`
  margin-left: 10px;
`;

interface INoEntityProps {
  entityType: EntityType;
}

const NoEntities = (props: INoEntityProps): React.ReactElement => {
  return (
    <NoEntityContainer>
      <NoEntityIcon entityType={props.entityType} />
      <NoEntityInstructions entityType={props.entityType} />
      <NoEntityAction entityType={props.entityType} />
    </NoEntityContainer>
  );
};

const NoEntityContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const NoEntityIcon = ({
  entityType,
}: {
  entityType: EntityType;
}): React.ReactElement => {
  switch (entityType) {
    case 'athletes':
      return <Icon size='large' name='person' />;
    case 'groups':
      return <Icon size='large' name='group' />;
  }
};

const NoEntityAction = ({
  entityType,
}: {
  entityType: EntityType;
}): React.ReactElement => {
  switch (entityType) {
    case 'athletes':
      return <AddAthletesPopup />;
    case 'groups':
      return <ManageGroupPopup />;
  }
};

const NoEntityInstructions = ({
  entityType,
}: {
  entityType: EntityType;
}): React.ReactElement => {
  switch (entityType) {
    case 'teams':

    case 'athletes':
      return <h1>Add Athletes</h1>;
    case 'groups':
      return <h1>Add Groups</h1>;
  }
};
